import React, {useState, useEffect} from 'react';
import Modal from './Modal';
import axiosInstance from "../utility/axiosInstance";
import {Instance} from '../store/slices/instancesSlice';
import {useToast} from "./ToastContext";
import Select from 'react-select';
import {useDispatch, useSelector} from "react-redux";
import {AppDispatch, RootState} from "../store/store";
import {fetchFlavors} from "../store/slices/flavorsSlice";
import { flavorLabel, isFlavorOptionDisabled } from '../utility/flavorOptions';
import { handleAxiosError } from '../helpers/handleAxiosError';

interface ResizeInstanceModalProps {
    isOpen: boolean;
    onClose: () => void;
    instance: Instance | null;
    onResizeStarted: () => void;
}

const ResizeInstanceModal: React.FC<ResizeInstanceModalProps> = ({isOpen, onClose, instance, onResizeStarted}) => {
    const {showToast} = useToast();
    const dispatch = useDispatch<AppDispatch>();
    const flavors = useSelector((state: RootState) => state.flavors.data);
    const flavorsStatus = useSelector((state: RootState) => state.flavors.status);

    const [resizing, setResizing] = useState(false);
    const [selectedFlavor, setSelectedFlavor] = useState<{ value: string; label: string; vcpus: number; ram: number } | null>(null);

    useEffect(() => {
        if (isOpen) {
            if (flavorsStatus === 'idle') {
                dispatch(fetchFlavors());
            }
            setSelectedFlavor(null);
        }
    }, [isOpen, flavorsStatus, dispatch]);

    const currentFlavor = flavors.find(f => f.id === instance?.flavor_id);

    const handleResize = async () => {
        if (!instance || !selectedFlavor) return;
        setResizing(true);
        try {
            await axiosInstance.post('/v1/instances/action', {
                instanceId: instance.id,
                action: 'RESIZE',
                flavorId: selectedFlavor.value
            });
            showToast("Instance resize initiated", "success");
            onResizeStarted();
            onClose();
        } catch (error) {
            console.error("Failed to resize instance:", error);
            // Same reason as CreateClusterModal: the server's message is the useful part.
            showToast(handleAxiosError(error) || "Failed to resize instance", "error");
        } finally {
            setResizing(false);
        }
    };

    const options = flavors
        .filter(f => {
            if (!currentFlavor) return true;
            const fVcpus = parseInt(f.vcpus);
            const fRam = parseInt(f.ram);
            const cVcpus = parseInt(currentFlavor.vcpus);
            const cRam = parseInt(currentFlavor.ram);
            // Must be larger in either vcpus or ram, and not smaller in either
            return (fVcpus >= cVcpus && fRam >= cRam) && (fVcpus > cVcpus || fRam > cRam);
        })
        .map((f: any) => ({
            value: f.id,
            label: flavorLabel(f),
            isDisabled: !!f.restricted,
            vcpus: parseInt(f.vcpus),
            ram: parseInt(f.ram)
        }));

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={`Resize Instance ${instance?.name}`} size="md">
            <div className="resize-modal-body" style={{padding: '20px'}}>
                <div style={{marginBottom: '20px'}}>
                    <label style={{display: 'block', marginBottom: '8px', fontWeight: 500}}>Current Flavor</label>
                    <input
                        type="text"
                        disabled
                        value={currentFlavor ? flavorLabel({ ...currentFlavor, restricted: false }) : 'N/A'}
                        style={{
                            width: '100%',
                            padding: '10px',
                            borderRadius: '6px',
                            border: '1px solid var(--border)',
                            background: 'var(--bg-table)',
                            color: 'var(--text-muted)'
                        }}
                    />
                </div>

                <div style={{marginBottom: '25px'}}>
                    <label style={{display: 'block', marginBottom: '8px', fontWeight: 500}}>New Flavor</label>
                    <Select
                        isOptionDisabled={isFlavorOptionDisabled as any}
                        options={options}
                        value={selectedFlavor}
                        onChange={(selected) => setSelectedFlavor(selected)}
                        placeholder="Select new flavor..."
                        classNamePrefix="react-select"
                    />
                    <p style={{fontSize: '0.875rem', color: 'var(--text-muted)', marginTop: '8px'}}>
                        Only flavors larger than the current one are shown.
                    </p>
                    <p style={{fontSize: '0.875rem', color: 'var(--text-muted)', marginTop: '8px'}}>
                        After resizing, confirm or revert the change. New size changes pricing.
                    </p>
                </div>

                <div className="modal-actions" style={{display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '30px'}}>
                    <button
                        className="btn-secondary"
                        onClick={onClose}
                        disabled={resizing}
                        style={{
                            padding: '8px 16px',
                            borderRadius: '6px',
                            border: '1px solid var(--border)',
                            background: 'var(--bg-card)',
                            cursor: 'pointer'
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        className="btn-primary"
                        onClick={handleResize}
                        disabled={resizing || !selectedFlavor}
                        style={{
                            padding: '8px 16px',
                            borderRadius: '6px',
                            border: 'none',
                            background: 'var(--action-primary-bg)',
                            color: 'var(--action-primary-text)',
                            cursor: (resizing || !selectedFlavor) ? 'not-allowed' : 'pointer',
                            opacity: (resizing || !selectedFlavor) ? 0.7 : 1
                        }}
                    >
                        {resizing ? 'Resizing...' : 'Resize Instance'}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default ResizeInstanceModal;
